import React from 'react';
import {
    Button,
    Grid,
    Input,
    Typography,
} from '@saddlebackchurch/react-cm-ui';
import makeStyles from '@saddlebackchurch/react-cm-ui/core/styles/makeStyles';
import ClassNames from 'classnames';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
    Currency,
    UIMode,
} from '../../../global/models';
import { EditableCurrencyAmount } from '../shared/models';
import CommitmentTitle from './commitmentTitle';
import { BEM_BLOCK_NAME } from './constants';
import getCurrencySymbol from './getCurrencySymbol';

type PropTypes = {
    campaignName: string;
    currency: Currency;
    goal: EditableCurrencyAmount | null;
    isDirtyInEditMode: boolean;
    locale: string;
    mode: UIMode;
    onCancelEditingGoal: () => void;
    onChangeGoalAmount: (goalAmountAsString: string) => void;
    onSaveCommitment: () => void;
};

const useStyles = makeStyles(({
    spacing,
    typography,
}) => ({
    amountInput: {
        '&.ui.input input': {
            paddingLeft: ({ extraWideCurrencySymbol }) => `${spacing(extraWideCurrencySymbol ? 5 : 2)}px`,
        },
        '&.ui.input.input-icon .input-actions > .input-icon-custom': {
            left: spacing(1),
            marginTop: '-11px',
            width: ({ extraWideCurrencySymbol }) => spacing(extraWideCurrencySymbol ? 4 : 1),
        },
    },
    editModeSaveButton: {
        marginBottom: spacing(1),
    },
    subtitle: {
        fontWeight: typography.fontWeightMedium,
    },
    title: {
        fontWeight: typography.fontWeightBold,
    },
}));

const CLASS_NAME_SETUP_COMMITMENT_CANCEL_BUTTON: string = `${BEM_BLOCK_NAME}--setup_commitment_cancel_button`;
const CLASS_NAME_SETUP_COMMITMENT_CONTAINER: string = `${BEM_BLOCK_NAME}--setup_commitment_container`;
const CLASS_NAME_SETUP_COMMITMENT_INPUT: string = `${BEM_BLOCK_NAME}--setup_commitment_input`;
const CLASS_NAME_SETUP_COMMITMENT_SAVE_BUTTON: string = `${BEM_BLOCK_NAME}--setup_commitment_save_button`;
const CLASS_NAME_SETUP_COMMITMENT_SUBTITLE: string = `${BEM_BLOCK_NAME}--setup_commitment_subtitle`;

function SetCommitment({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    campaignName, // not being used because Dream Now is hard-coded in translations
    currency,
    goal,
    isDirtyInEditMode,
    locale,
    mode,
    onCancelEditingGoal,
    onChangeGoalAmount,
    onSaveCommitment,
}: PropTypes) {
    const classes = useStyles({ extraWideCurrencySymbol: true });

    const { t } = useTranslation();
    const hasValidAmount: boolean = !isNil(goal?.amount) && goal.amount > 0;

    const isSaveButtonDisabled: boolean =
        !hasValidAmount ||
        (mode === UIMode.Edit && !isDirtyInEditMode);

    const amountInputClassNames = ClassNames(
        classes.amountInput,
        CLASS_NAME_SETUP_COMMITMENT_INPUT,
    );

    const subtitleClassNames = ClassNames(classes.subtitle, CLASS_NAME_SETUP_COMMITMENT_SUBTITLE);

    const saveButtonClassNames = ClassNames(CLASS_NAME_SETUP_COMMITMENT_SAVE_BUTTON, {
        [classes.editModeSaveButton]: mode === UIMode.Edit,
    });

    return (
        <Grid
            className={CLASS_NAME_SETUP_COMMITMENT_CONTAINER}
            spacing={2}
        >
            <Grid.Column sm={12}>
                <CommitmentTitle />
            </Grid.Column>

            <Grid.Column sm={12}>
                <Typography
                    className={subtitleClassNames}
                    variant="body2"
                >
                    {t('common.infoContainer.signedInInfoText')}
                </Typography>
            </Grid.Column>

            <Grid.Column sm={12}>
                <Input
                    allowDecimals
                    allowNegativeNumbers={false}
                    className={amountInputClassNames}
                    dataTestId={CLASS_NAME_SETUP_COMMITMENT_INPUT}
                    fluid
                    icon={(
                        <Typography
                            component="span"
                            variant="body1"
                        >
                            {getCurrencySymbol(currency, locale)}
                        </Typography>
                    )}
                    id={CLASS_NAME_SETUP_COMMITMENT_INPUT}
                    max={10000000}
                    min={0}
                    onChange={onChangeGoalAmount}
                    showSpinners={false}
                    type="number"
                    value={goal?.amountAsString ?? ''}
                />
            </Grid.Column>

            <Grid.Column sm={12}>
                <React.Fragment>
                    <Button
                        className={saveButtonClassNames}
                        color="primary"
                        disabled={isSaveButtonDisabled}
                        fluid
                        id={CLASS_NAME_SETUP_COMMITMENT_SAVE_BUTTON}
                        onClick={onSaveCommitment}
                    >
                        <span>
                            {mode === UIMode.Add ? t('common.infoContainer.saveCommitmentButton') : t('common.infoContainer.updateCommitmentButton')}
                        </span>
                    </Button>

                    {mode === UIMode.Edit && (
                        <Button
                            className={CLASS_NAME_SETUP_COMMITMENT_CANCEL_BUTTON}
                            color="alternate"
                            fluid
                            id={CLASS_NAME_SETUP_COMMITMENT_CANCEL_BUTTON}
                            onClick={onCancelEditingGoal}
                        >
                            <span>{t('common.infoContainer.cancelButton')}</span>
                        </Button>
                    )}
                </React.Fragment>
            </Grid.Column>
        </Grid>
    );
}

export default SetCommitment;
